import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLineItemCustomFieldNames } from "@/store/quoteSettingsStore";
import { itemNumberingOptions } from "./lineFields";

/** Sentinel Select value standing for sequential numbering (Radix forbids ""). */
const SEQUENTIAL_VALUE = "__sequential";

interface ItemNumberingSelectProps {
  /** The block's authored `number-source`, or "" when it numbers sequentially. */
  value: string;
  /** Writes (or clears, on "") the `number-source` attribute on the selected block. */
  onChange: (numberSource: string) => void;
}

/**
 * The picker for a line repeater's `number-source`. It is the bespoke control
 * the prop schema's override map points `number-source` at: the org's line
 * custom fields are read at runtime, so the generated select cannot enumerate
 * them. Sources are picked rather than typed, so a misspelled field name cannot
 * silently fall back to sequential numbering.
 * @param value - The block's authored number source
 * @param onChange - Writes or clears the number source
 * @returns The item numbering picker
 */
export function ItemNumberingSelect({
  value,
  onChange,
}: ItemNumberingSelectProps) {
  const customFieldNames = useLineItemCustomFieldNames();
  const options = itemNumberingOptions(customFieldNames, value);

  return (
    <div>
      <Label className="mb-1 block text-[11px]">Item numbering</Label>
      <Select
        value={value === "" ? SEQUENTIAL_VALUE : value}
        onValueChange={(next) =>
          onChange(next === SEQUENTIAL_VALUE ? "" : next)
        }
      >
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={SEQUENTIAL_VALUE}>Sequential (1, 2, 3)</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.source} value={option.source}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {customFieldNames.length === 0 && (
        <p className="mt-1 text-[11px] text-muted-foreground">
          Add line item custom fields in quote settings to number lines from one
          of them.
        </p>
      )}
    </div>
  );
}
